<template>
  <form
    class="relative mb-8 grid gap-6 border-4 border-neutral-50 p-6 pb-14 text-center"
    autocomplete="off"
    @submit="onSubmit"
  >
    <h3>{{ $t('Your orders / return documents') }}</h3>

    <div>
      <KikLabel
        class="text-left"
        label-for="orderNumber"
        :label="$t('Order number')"
      >
        <SfInput
          id="orderNumber"
          v-model="orderNumber"
          name="orderNumber"
          data-testid="login__order-number-input"
          :placeholder="$t('Order number')"
          v-bind="orderNumberAttrs"
          :invalid="!!errors.orderNumber"
        />
      </KikLabel>
      <KikFormError
        class="relative mt-1 text-left"
        :error="errors.orderNumber"
      />
    </div>

    <div>
      <KikLabel
        class="text-left"
        label-for="orderEmail"
        :label="$t('E-mail address')"
      >
        <SfInput
          id="orderEmail"
          v-model="orderEmail"
          name="orderEmail"
          data-testid="login__order-email-input"
          type="email"
          :placeholder="$t('E-mail address')"
          v-bind="orderEmailAttrs"
          :invalid="!!errors.orderEmail"
        />
      </KikLabel>
      <KikFormError
        class="relative mt-1 text-left"
        :error="errors.orderEmail"
      />
    </div>

    <section
      v-if="error"
      class="mb-4 rounded border border-primary-400 bg-primary-100 p-4 text-left text-primary-700"
      data-testid="login__order-error-message"
    >
      {{ $t(error) }}
    </section>
    <div class="absolute inset-x-0 -bottom-6 me-auto ms-auto w-[320px]">
      <div class="bg-white text-center">
        <KikButton
          :uppercase="true"
          class="h-[48px] min-w-[280px] !text-neutral-900 hover:!bg-neutral-100"
          variant="secondary"
          data-testid="login__order-submit-btn"
          v-bind="{ type: 'submit' }"
          :disabled="isSubmitting"
        >
          <SfLoaderCircular v-if="isSubmitting" />
          <span v-else>{{ $t('Continue') }}</span>
        </KikButton>
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import {
  SfInput,
  SfLoaderCircular,
} from '@storefront-ui/vue';
import * as zod from 'zod';

import { useBFF } from '~/composables/useBFF/useBFF';

const { $i18n, $router } = useNuxtApp();
const { $orders } = useBFF();
const { closeLoginDrawer } = useUiState();

const { defineField, handleSubmit, errors, isSubmitting } = useForm({
  validationSchema: toTypedSchema(
    zod.object({
      orderNumber: zod.string({ message: $i18n.t('This field is required.') }).min(1, { message: $i18n.t('This field is required.') }).regex(/^\d+$/, { message: $i18n.t('This field should only contain numbers.') }),
      orderEmail: zod.string({ message: 'This field is required.' }).min(1, { message: 'This field is required.' }).email({ message: 'The e-mail address is incorrect.' }),
    }),
  ),
});

const [orderNumber, orderNumberAttrs] = defineField('orderNumber');
const [orderEmail, orderEmailAttrs] = defineField('orderEmail');

const error = ref();

const closeModal = () => {
  closeLoginDrawer();
  navigateTo($router.currentRoute.value.path);
};

const onSubmit = handleSubmit(async (values) => {
  error.value = undefined;

  if (!values.orderNumber || !values.orderEmail) {
    return;
  }

  try {
    const { data } = await $orders.getOrderByNumberAndEmail({ orderNumber: values.orderNumber, customerEmail: values.orderEmail });

    if (!data) {
      throw new Error();
    }

    closeModal();
    navigateTo(`/guestOrderDetails/?orderId=${data}`);
  }
  catch (e: unknown) {
    error.value = 'We can\'t find any order matching your provided data.';
    throw e;
  }
});
</script>
